import { useCallback, useEffect, useState } from 'react'
import { supabase, readableError } from '../../lib/supabase'
import { useSession } from '../../lib/session'
import { sortByUrgency, urgencyKey } from '../../lib/licence'
import { Shell } from '../../components/Shell'
import { Spinner } from '../../components/ui'
import LicenceList from '../../components/LicenceList'

/**
 * The songwriter's mirror of routes/admin/Endorsements.jsx — same
 * component, same vocabulary, opposite perspective. Which songs are
 * licensed to which church, and when each is up for approval or renewal.
 *
 * Copyright never moves through any of this — a licence is an endorsement
 * plus permission, not a transfer. See lib/licence.js.
 */

export default function Licences() {
  const { user } = useSession()
  const [rows, setRows] = useState(null)
  const [err, setErr] = useState(null)
  const [decidingId, setDecidingId] = useState(null)
  const [events, setEvents] = useState({})

  const load = useCallback(async () => {
    if (!user) return
    const { data, error } = await supabase
      .from('song_licences')
      .select('*, songs(title), churches(name)')
      .eq('songwriter_id', user.id)
    if (error) setErr(readableError(error))
    setRows(data ?? [])

    // The decision log, fetched here rather than in LicenceList — that
    // component deliberately queries nothing. Grouped by licence so each row
    // can render its own history without a second round trip.
    const ids = (data ?? []).map((r) => r.id)
    if (ids.length === 0) return setEvents({})
    const { data: evs } = await supabase
      .from('song_licence_events')
      .select('id, licence_id, event, reason, occurred_at')
      .in('licence_id', ids)
      .order('occurred_at', { ascending: false })
    setEvents((evs ?? []).reduce((acc, e) => {
      ;(acc[e.licence_id] ||= []).push(e)
      return acc
    }, {}))
  }, [user])

  useEffect(() => { load() }, [load])

  // Letting it end is the one that can't be taken back — approving again just
  // starts another term, so only the decline asks twice.
  const reapprove = async (row, approve) => {
    if (!approve && !window.confirm(
      `Let the licence for "${row.song_title || 'this song'}" end? ${row.church_name || 'The church'} `
      + 'stops using it, and you would have to propose a new licence to undo this.'
    )) return
    setDecidingId(row.id)
    setErr(null)
    const { error } = await supabase.rpc('decide_licence_reapproval', {
      p_licence_id: row.id, p_approve: approve,
    })
    setDecidingId(null)
    if (error) return setErr(readableError(error))
    load()
  }

  // Confirmed twice on purpose: it is an escalation, the church is told, and
  // the reason the writer typed travels with it.
  const withdraw = async (row, reason, makePrivate) => {
    if (!window.confirm(
      `Withdraw the licence for "${row.song_title || 'this song'}"? `
      + `${row.church_name || 'The church'} keeps using it for 7 days, then it goes inactive.`
      + (makePrivate ? ' The song will also be made private, hiding it from everyone.' : '')
    )) return
    setDecidingId(row.id)
    setErr(null)
    const { error } = await supabase.rpc('withdraw_licence', {
      p_licence_id: row.id, p_reason: reason, p_make_private: makePrivate,
    })
    setDecidingId(null)
    if (error) return setErr(readableError(error))
    load()
  }

  const reactivate = async (row) => {
    setDecidingId(row.id)
    setErr(null)
    const { error } = await supabase.rpc('reactivate_licence', { p_licence_id: row.id })
    setDecidingId(null)
    if (error) return setErr(readableError(error))
    load()
  }

  if (!user || rows === null) return <Shell back="/me" title="Your songs"><Spinner /></Shell>

  const shaped = sortByUrgency(rows).map((r) => ({
    ...r,
    song_title: r.songs?.title,
    church_name: r.churches?.name,
  }))

  const upcoming = shaped.filter((r) => urgencyKey(r)[0] <= 1)
  const settled = shaped.filter((r) => urgencyKey(r)[0] > 1)

  return (
    <Shell back="/me" title="Your songs">
      <div className="eyebrow">Songwriter</div>
      <h2 className="q">Licences</h2>
      <p className="sub">
        Which of your songs are licensed to which church, and when each one is next up for
        approval or renewal.
      </p>

      <div className="eyebrow" style={{ marginTop: 22 }}>Needs your decision, or renewing soon</div>
      <LicenceList rows={upcoming} perspective="writer"
                   emptyLabel="Nothing waiting on you and nothing renewing soon."
                   onReapprove={reapprove} decidingId={decidingId} events={events}
                   onWithdraw={withdraw} onReactivate={reactivate} />

      <div className="eyebrow" style={{ marginTop: 26 }}>Everything else</div>
      <LicenceList rows={settled} perspective="writer"
                   emptyLabel="No other endorsed songs yet."
                   decidingId={decidingId} events={events}
                   onReactivate={reactivate} />

      {err && <p className="err">{err}</p>}
    </Shell>
  )
}
